import classNames from "classnames";
import React, { ChangeEvent, Component, FocusEvent } from "react";
import { FormField, FormFieldProps } from "../form-field";
import { AriaProps, WidthOption } from "../types";
import { Radio, RadioProps } from "./radio";
import { CSS_CLASSES } from "./radio-group-css-classes";

let nextUniqueId = 0;

const getNextUniqueId = () => `fbds-input-${++nextUniqueId}`;

export interface RadioGroupItem {
  id?: string;
  label?: string;
  value: string;
}

export interface RadioGroupOwnProps {
  /**
   * Specify the value of the controlled input element.
   */
  value?: string;

  /**
   * Specify the change event handler function for the controlled input element.
   */
  onChange: (value: string) => void;

  /**
   * Array of objects with props to be passed to each of the underlying Radio
   * components.
   */
  radios: RadioGroupItem[];

  /**
   * Determines the orientation along which the underlying Radio components
   * are layed out.
   *
   * @default
   */
  orientation?: "horizontal" | "vertical";

  /**
   * Specify the width of the radio group. Supported values are:
   * - auto: width is dependent on the content of this component.
   * - full: makes it occupy the full width of the parent irrespective of content width.
   *
   * @default auto
   */
  groupWidth?: WidthOption;

  /**
   * Specify the `width` prop of underlying `Radio` components.
   * Width will be responsible for rendering individual radios in different widths supported:
   * auto: will render radio as per the length of the label and increase with as label gets bigger
   * full: will render the radio occupying full available width
   *
   * @default auto
   */
  radioWidth?: WidthOption;

  /**
   * Determines the spacing between the underlying Radio components.
   *
   * @default start
   */
  justify?: "start" | "end" | "center" | "between" | "around" | "evenly";

  /**
   * If true, render the input component within a FormField.
   *
   * @default false
   */
  formField?: boolean;

  /**
   * Apply/override styling for the FormField wrapping the Input component
   * by passing in custom CSS class-name(s) using this prop.
   */
  formFieldClassName?: string;

  /**
   * Apply/override styling to each of the underlying Radio components by
   * passing in custom CSS class-name(s) using this prop.
   */
  radioClassName?: string;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop.
   */
  className?: string;

  /**
   * id for selecting element for automated tests
   */
  "data-testid"?: string;
}

export type RadioGroupProps = RadioGroupOwnProps &
  Omit<RadioProps, "value" | "onChange" | "checked" | "label" | "hideLabel" | "width" | "className"> &
  Omit<FormFieldProps, keyof RadioGroupOwnProps | "formFieldId" | "children" | "focused" | "textLength">;

export interface RadioGroupState {
  focused: boolean;
}

/**
 * A `RadioGroup` represents a set of related options from which a user can select only one.
 * It composes two or more `Radio` components under the hood.
 *
 * **Note**: This component re-renders as many times as the number of child items in each interaction. Thus,
 * if this behavior is to be avoided, please use `Radio` component and control them independently.
 */
export class RadioGroup extends Component<RadioGroupProps> {
  static defaultProps = {
    justify: "start",
    groupWidth: "auto",
    radioWidth: "auto",
    orientation: "vertical",
    formField: false,
  };

  groupId = this.props.id || getNextUniqueId();

  constructor(props: RadioGroupProps) {
    super(props);

    this.groupId = this.props.id || getNextUniqueId();
  }

  state: RadioGroupState = {
    focused: false,
  };

  handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.props.onChange && this.props.onChange(event.target.value);
  };

  handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    this.setState({ focused: true });
    this.props.onFocus && this.props.onFocus(event);
  };

  handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    this.setState({ focused: false });
    this.props.onBlur && this.props.onBlur(event);
  };

  render() {
    const {
      /* Input Base props */
      id,
      name,
      required,
      disabled,
      readOnly,
      onChange,
      onFocus,
      onBlur,
      /* Radio props */
      labelPlacement,
      color,
      icons,
      /* Own props */
      value,
      radios,
      orientation,
      groupWidth,
      radioWidth,
      justify,
      formField,
      formFieldClassName: formFieldClassNameProp,
      radioClassName: radioClassNameProp,
      className,
      "data-testid": testId,
      /* FormField pass through props */
      formFieldMessageId,
      label,
      labelInfo,
      hideLabel,
      secondaryAction,
      hint,
      success,
      error,
      /* Rest props */
      ...restProps
    } = this.props;

    const rootClassName = classNames(
      {
        [CSS_CLASSES.RADIO_GROUP_ROOT_HORIZONTAL]: orientation === "horizontal",
        [CSS_CLASSES.RADIO_GROUP_ROOT_VERTICAL]: orientation === "vertical",
        [CSS_CLASSES.RADIO_GROUP_ROOT_FULL]: groupWidth === "full",
        [CSS_CLASSES.RADIO_GROUP_ROOT_AUTO]: groupWidth === "auto",
        [CSS_CLASSES.RADIO_GROUP_ROOT_JUSTIFY_START]: justify === "start",
        [CSS_CLASSES.RADIO_GROUP_ROOT_JUSTIFY_END]: justify === "end",
        [CSS_CLASSES.RADIO_GROUP_ROOT_JUSTIFY_CENTER]: justify === "center",
        [CSS_CLASSES.RADIO_GROUP_ROOT_JUSTIFY_BETWEEN]: justify === "between",
        [CSS_CLASSES.RADIO_GROUP_ROOT_JUSTIFY_AROUND]: justify === "around",
        [CSS_CLASSES.RADIO_GROUP_ROOT_JUSTIFY_EVENLY]: justify === "evenly",
      },
      className
    );

    const formFieldClassName = classNames(
      {
        [CSS_CLASSES.RADIO_GROUP_FORM_FIELD_FULL]: groupWidth === "full",
      },
      formFieldClassNameProp
    );

    const radioClassName = classNames(
      {
        [CSS_CLASSES.RADIO_GROUP_ITEM_HORIZONTAL]: orientation === "horizontal",
        [CSS_CLASSES.RADIO_GROUP_ITEM_VERTICAL]: orientation === "vertical",
      },
      radioClassNameProp
    );

    const getRadioGroupElement = ({
      "aria-labelledby": ariaLabelledby,
      "aria-label": ariaLabel,
      ...ariaProps
    }: AriaProps = {}) => (
      <div
        id={this.groupId}
        data-testid={testId}
        role="group"
        aria-labelledby={ariaLabelledby}
        aria-label={ariaLabel}
        className={rootClassName}
      >
        {radios.map(({ id: radioGroupItemId, label: radioLabel, value: radioValue }) => {
          const radioId = radioGroupItemId || `${this.groupId}-${radioValue}`;
          return (
            <Radio
              key={radioId}
              id={radioId}
              name={name ?? label ?? this.groupId}
              value={radioValue}
              required={false}
              disabled={disabled}
              readOnly={readOnly}
              onChange={this.handleChange}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              checked={radioValue === value}
              label={radioLabel || radioValue}
              hideLabel={false}
              labelPlacement={labelPlacement}
              width={radioWidth}
              color={color}
              icons={icons}
              className={radioClassName}
              {...restProps}
              {...ariaProps}
            />
          );
        })}
      </div>
    );

    return formField ? (
      <FormField
        formFieldId={this.groupId}
        formFieldMessageId={formFieldMessageId}
        label={label}
        labelInfo={labelInfo}
        hideLabel={hideLabel}
        secondaryAction={secondaryAction}
        hint={hint}
        success={success}
        error={error}
        focused={this.state.focused}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        className={formFieldClassName}
      >
        {getRadioGroupElement}
      </FormField>
    ) : (
      getRadioGroupElement()
    );
  }
}
